import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from "../utils";
import Swal from 'sweetalert2'
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUser, FiMail, FiPhone, FiMapPin, FiCalendar, FiLock,
  FiCamera, FiUpload, FiCheck, FiAlertCircle, FiSave,
  FiUserPlus, FiHeart, FiBank, FiCreditCard,
  FiShield, FiArrowRight, FiRefreshCw, FiHome, FiBriefcase
} from 'react-icons/fi';
import {
  FaUserCircle, FaIdCard,  FaGooglePay, FaPhoneAlt, FaMapMarkerAlt, FaCity, FaFlag, FaCode,
  FaUserFriends, FaHandshake, FaBuilding, FaBirthdayCake,
  FaFileAlt, FaCheckCircle, FaShieldAlt,FaEnvelope,
} from 'react-icons/fa';

// Validation functions
const validateIFSC = (value) => {
  if (!value) return true;
  const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  return ifscPattern.test(value) || 'Invalid IFSC format (e.g., HDFC0123456)';
};

const validatePAN = (value) => {
  if (!value) return true;
  const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panPattern.test(value) || 'Invalid PAN format (e.g., ABCDE1234F)';
};

const validatePincode = (value) => {
  if (!value) return true;
  const pincodePattern = /^\d{6}$/;
  return pincodePattern.test(value) || 'Pincode must be 6 digits';
};

const validateBankAccount = (value) => {
  if (!value) return true;
  const accountPattern = /^\d{9,18}$/;
  return accountPattern.test(value) || 'Account number must be 9-18 digits';
};

const validateNomineeAge = (value) => {
  if (!value) return true;
  const age = parseInt(value);
  return (age >= 1 && age <= 120) || 'Age must be between 1 and 120';
};

const UpdateProfile = () => {
  const { register, handleSubmit, formState: { errors }, watch, setError, reset } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isKYCUpdate, setisKYCUpdate] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const userData = useSelector(state => state.user_info[0]?.user);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeSection, setActiveSection] = useState('personal');
  const navigate = useNavigate();

  // Handle image preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Swal.fire({
          title: 'File Too Large',
          text: 'Image size should be less than 2MB',
          icon: 'warning',
          confirmButtonColor: '#2AADCC'
        });
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const fetchKYCHistory = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/kyc/history/${userData._id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        const data = await response.json();
        if (data.success) {
          setisKYCUpdate(data.data);
        }
      } catch (error) {
        console.error('Error fetching KYC history:', error);
      }
    };

    fetchKYCHistory();
  }, [userData?._id]);

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      let imageUrl = userData?.profile_image || null;
      
      if (profileImage) {
        const formData = new FormData();
        formData.append('file', profileImage);
        formData.append('upload_preset', 'prayagproperties');

        const cloudinaryResponse = await fetch(
          'https://api.cloudinary.com/v1_1/djmyigser/image/upload',
          {
            method: 'POST',
            body: formData
          }
        );

        const cloudinaryData = await cloudinaryResponse.json();
        if (!cloudinaryData.secure_url) {
          throw new Error('Failed to upload image to Cloudinary');
        }
        imageUrl = cloudinaryData.secure_url;
      }

      const nominee_age = data.nominee_age ? parseInt(data.nominee_age) : undefined;

      const payload = {
        ...data,
        profile_image: imageUrl,
        nominee_age,
        ifsc_code: data.ifsc_code?.toUpperCase(),
        pan_number: data.pan_number?.toUpperCase()
      };

      const response = await sendApiRequest('api/auth/update-profile', payload, "PUT");

      if (response.data.success) {
        Swal.fire({
          title: 'Profile Updated Successfully!',
          text: 'Your profile has been updated.',
          icon: 'success',
          confirmButtonText: 'Go to Dashboard',
          confirmButtonColor: '#2AADCC',
          background: '#ffffff',
          timer: 2000,
          timerProgressBar: true
        }).then(() => {
          navigate('/dashboard');
        });
      } else {
        Swal.fire({
          title: 'Update Failed',
          text: response.data.message || 'Failed to update profile',
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#2AADCC'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Upload Error',
        text: error.message || 'Failed to upload image',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2AADCC'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sections = [
    { id: 'personal', label: 'Personal Info', icon: FiUser, color: 'indigo' },
    { id: 'nominee', label: 'Nominee Details', icon: FaUserFriends, color: 'purple' },
    { id: 'bank', label: 'Bank Details', icon: FaEnvelope, color: 'emerald' },
    { id: 'wallet', label: 'Wallet Details', icon: FaEnvelope, color: 'orange' }
  ];

  const inputClasses = "w-full px-4 py-3 text-sm rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 outline-none bg-white";
  const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";
  const errorClasses = "text-xs text-red-500 mt-1 flex items-center gap-1";
  const sectionClasses = "bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden";
  const headingClasses = "text-lg font-bold text-gray-800 mb-4 pb-3 border-b-2 border-indigo-100 flex items-center gap-2";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Update Profile
              </h1>
              <p className="text-gray-500 mt-1 text-sm">Keep your information up to date</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Secure Form</span>
              </span>
            </div>
          </div>
        </div>

        {isKYCUpdate.length > 0 ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Profile Image Section - Hero Style */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <FiCamera className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Profile Picture</h3>
                    <p className="text-indigo-100 text-sm">Upload your photo for verification</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="relative">
                    {imagePreview || userData?.profile_image ? (
                      <div className="relative group">
                        <img
                          src={imagePreview || userData?.profile_image}
                          alt="Profile Preview"
                          className="w-28 h-28 rounded-full object-cover border-4 border-indigo-100 shadow-lg"
                        />
                        <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                          <FiCamera className="w-6 h-6 text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-28 h-28 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center border-4 border-indigo-100 shadow-lg">
                        <FaUserCircle className="w-16 h-16 text-indigo-400" />
                      </div>
                    )}
                    <label
                      htmlFor="profileImage"
                      className="absolute bottom-0 right-0 cursor-pointer bg-indigo-600 text-white p-2 rounded-full shadow-lg hover:bg-indigo-700 transition"
                    >
                      <FiCamera className="w-3 h-3" />
                    </label>
                    <input
                      type="file"
                      id="profileImage"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h4 className="font-semibold text-gray-800">{userData?.fullName}</h4>
                    <p className="text-sm text-gray-500 mt-1">{userData?.sponsorCode}</p>
                    <p className="text-xs text-gray-400 mt-2">Max file size: 2MB (JPEG, PNG)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Navigation */}
            <div className="flex flex-wrap gap-2 sticky top-0 z-10 bg-white/90 backdrop-blur-sm p-2 rounded-xl shadow-sm">
              {sections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                    activeSection === section.id
                      ? `bg-gradient-to-r from-${section.color}-600 to-${section.color}-500 text-white shadow-md`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  <span className="text-sm">{section.label}</span>
                </button>
              ))}
            </div>

            {/* Personal Details Section */}
            <AnimatePresence mode="wait">
              {activeSection === 'personal' && (
                <motion.div
                  key="personal"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={sectionClasses}
                >
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <FiUser className="w-5 h-5 text-indigo-600" />
                      Personal Information
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Update your personal details</p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {[
                        { name: 'fullName', label: 'Full Name', icon: FiUser, type: 'text', placeholder: 'Enter your full name', required: true },
                        { name: 'phone', label: 'Phone Number', icon: FiPhone, type: 'tel', placeholder: 'Enter phone number', required: true },
                        { name: 'email', label: 'Email Address', icon: FiMail, type: 'email', placeholder: 'Enter email address', required: true },
                        { name: 'dob', label: 'Date of Birth', icon: FaBirthdayCake, type: 'date', placeholder: 'Select date', required: false },
                        { name: 'address', label: 'Address', icon: FiMapPin, type: 'text', placeholder: 'Enter your address', required: false },
                        { name: 'city', label: 'City', icon: FaCity, type: 'text', placeholder: 'Enter city', required: false },
                        { name: 'state', label: 'State', icon: FaBuilding, type: 'text', placeholder: 'Enter state', required: false },
                        { name: 'country', label: 'Country', icon: FaFlag, type: 'text', placeholder: 'Enter country', required: false },
                        { name: 'pincode', label: 'Pin Code', icon: FiLock, type: 'text', placeholder: 'Enter pincode', required: false, validate: validatePincode },
                        { name: 'father_name', label: "Father's Name", icon: FaHandshake, type: 'text', placeholder: "Enter father's name", required: false }
                      ].map((field) => (
                        <div key={field.name} className="space-y-1">
                          <label className={labelClasses}>
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <div className="relative">
                            <field.icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                              {...register(field.name, field.validate ? { validate: field.validate } : {})}
                              type={field.type}
                              placeholder={field.placeholder}
                              className={`${inputClasses} pl-10 ${errors[field.name] ? 'border-red-500' : ''}`}
                            />
                          </div>
                          {errors[field.name] && (
                            <p className={errorClasses}>
                              <FiAlertCircle className="w-3 h-3" />
                              {errors[field.name].message}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Nominee Details Section */}
              {activeSection === 'nominee' && (
                <motion.div
                  key="nominee"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={sectionClasses}
                >
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <FaUserFriends className="w-5 h-5 text-purple-600" />
                      Nominee Details
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Information about your nominee</p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {[
                        { name: 'nominee_name', label: 'Nominee Name', icon: FaUserFriends, type: 'text', placeholder: 'Enter nominee name' },
                        { name: 'nominee_age', label: 'Age', icon: FiCalendar, type: 'number', placeholder: 'Enter age', validate: validateNomineeAge },
                        { name: 'nominee_relation', label: 'Relation', icon: FaHandshake, type: 'select', placeholder: 'Select relation', options: ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'] },
                        { name: 'nominee_phone', label: 'Mobile Number', icon: FiPhone, type: 'tel', placeholder: 'Enter nominee mobile' }
                      ].map((field) => (
                        <div key={field.name} className="space-y-1">
                          <label className={labelClasses}>{field.label}</label>
                          <div className="relative">
                            <field.icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            {field.type === 'select' ? (
                              <select
                                {...register(field.name)}
                                className={`${inputClasses} pl-10 cursor-pointer`}
                              >
                                <option value="">{field.placeholder}</option>
                                {field.options?.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                {...register(field.name, field.validate ? { validate: field.validate } : {})}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`${inputClasses} pl-10 ${errors[field.name] ? 'border-red-500' : ''}`}
                              />
                            )}
                          </div>
                          {errors[field.name] && (
                            <p className={errorClasses}>
                              <FiAlertCircle className="w-3 h-3" />
                              {errors[field.name].message}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Bank Details Section */}
              {activeSection === 'bank' && (
                <motion.div
                  key="bank"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={sectionClasses}
                >
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <FaEnvelope className="w-5 h-5 text-emerald-600" />
                      Bank Details
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Your banking information for withdrawals</p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {[
                        { name: 'bank_name', label: 'Bank Name', icon: FaEnvelope, type: 'text', placeholder: 'Enter bank name' },
                        { name: 'account_holder_name', label: 'Account Holder Name', icon: FiUser, type: 'text', placeholder: 'Enter account holder name' },
                        { name: 'branch_name', label: 'Branch Name', icon: FaBuilding, type: 'text', placeholder: 'Enter branch name' },
                        { name: 'bank_account_number', label: 'Account Number', icon: FiCreditCard, type: 'text', placeholder: 'Enter account number', validate: validateBankAccount },
                        { name: 'ifsc_code', label: 'IFSC Code', icon: FaCode, type: 'text', placeholder: 'Enter IFSC code', validate: validateIFSC },
                        { name: 'pan_number', label: 'PAN Number', icon: FiShield, type: 'text', placeholder: 'Enter PAN number', validate: validatePAN },
                        { name: 'mobile_number', label: 'Mobile Number', icon: FiPhone, type: 'tel', placeholder: 'Enter mobile number' },
                        { name: 'account_type', label: 'Account Type', icon: FaFileAlt, type: 'select', placeholder: 'Select account type', options: ['Savings', 'Current', 'Joint'] }
                      ].map((field) => (
                        <div key={field.name} className="space-y-1">
                          <label className={labelClasses}>{field.label}</label>
                          <div className="relative">
                            <field.icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            {field.type === 'select' ? (
                              <select
                                {...register(field.name)}
                                className={`${inputClasses} pl-10 cursor-pointer`}
                              >
                                <option value="">{field.placeholder}</option>
                                {field.options?.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                {...register(field.name, field.validate ? { validate: field.validate } : {})}
                                type={field.type}
                                placeholder={field.placeholder}
                                className={`${inputClasses} pl-10 ${errors[field.name] ? 'border-red-500' : ''}`}
                              />
                            )}
                          </div>
                          {errors[field.name] && (
                            <p className={errorClasses}>
                              <FiAlertCircle className="w-3 h-3" />
                              {errors[field.name].message}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Security Notice */}
                    <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <div className="flex items-start gap-3">
                        <FiShield className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Secure Banking Information</p>
                          <p className="text-xs text-amber-700 mt-1">Your bank details are encrypted and securely stored. We never share your information.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Wallet Details Section */}
              {activeSection === 'wallet' && (
                <motion.div
                  key="wallet"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className={sectionClasses}
                >
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      <FaEnvelope className="w-5 h-5 text-orange-600" />
                      Wallet & UPI Details
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">Your UPI IDs for quick payments</p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {[
                        { name: 'phonepeId', label: 'PhonePe ID/Mobile no', icon: FaGooglePay, type: 'text', placeholder: 'Enter PhonePe ID or mobile number', brand: 'phonepe' },
                        { name: 'googlePayId', label: 'Google Pay ID/Mobile no', icon: FaGooglePay, type: 'text', placeholder: 'Enter Google Pay ID or mobile number', brand: 'gpay' }
                      ].map((field) => (
                        <div key={field.name} className="space-y-1">
                          <label className={labelClasses}>{field.label}</label>
                          <div className="relative">
                            <div className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center ${field.brand === 'phonepe' ? 'bg-indigo-500' : 'bg-emerald-500'}`}>
                              <field.icon className="w-3 h-3 text-white" />
                            </div>
                            <input
                              {...register(field.name)}
                              type={field.type}
                              placeholder={field.placeholder}
                              className={`${inputClasses} pl-12`}
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">You can enter either UPI ID or registered mobile number</p>
                        </div>
                      ))}
                    </div>

                    {/* Payment Info */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex items-start gap-3">
                        <FiCheck className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-blue-800">Quick & Secure Payments</p>
                          <p className="text-xs text-blue-700 mt-1">All payments are processed through secure UPI channels. Your payment information is protected.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit Button */}
            <div className="sticky bottom-4 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
                <div className="text-sm text-gray-500">
                  <FiShield className="inline mr-1 w-4 h-4" />
                  Your information is secure
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      Update Profile
                      <FiArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
              <FiAlertCircle className="w-10 h-10 text-amber-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">KYC Not Submitted</h3>
            <p className="text-gray-500 mb-6">You have not submitted any KYC yet. Please submit your KYC for verification.</p>
            <button
              onClick={() => navigate('/kyc')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition"
            >
              Submit KYC Now
              <FiArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateProfile;